import { storage } from '../config/firebaseConfig.js';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { api } from './api.js';
import { ENDPOINTS } from '../config/endpoints.js';

export const photoService = {
  /**
   * Faz upload de uma foto e atualiza o array de imagens da entidade
   * 
   * @param {File} file - Arquivo da foto
   * @param {string} entityId - ID da entidade
   * @param {number} position - Posição onde a imagem será inserida (0-4)
   * @param {Array} currentImages - Array atual de imagens da entidade (do retorno da API)
   * @returns {Promise<{success: boolean, url?: string, images?: Array, error?: string}>}
   */
  async uploadPhoto(file, entityId, position, currentImages) {
    try {
      // Validar tipo de arquivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return { 
          success: false, 
          error: 'Formato inválido. Use JPG, PNG ou WEBP.' 
        };
      }

      // Validar tamanho (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        return { 
          success: false, 
          error: 'Arquivo muito grande. Tamanho máximo: 5MB.' 
        };
      }

      // Validar posição
      if (position < 0 || position > 4) {
        return {
          success: false,
          error: 'Posição inválida. Use um valor entre 0 e 4.'
        };
      }

      // 1. UPLOAD DO ARQUIVO PARA FIREBASE
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storagePath = `entities/${entityId}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      console.log('📸 URL do Firebase:', downloadURL);
      console.log('📋 Imagens atuais recebidas:', currentImages);

      // 2. PREPARAR LISTA COMPLETA DE 5 POSIÇÕES
      const imagesList = this.buildImagesList(currentImages || [], entityId);
      
      console.log('📝 Lista preparada ANTES de adicionar nova imagem:', imagesList);

      // 3. COLOCAR A IMAGEM NA POSIÇÃO ESPECIFICADA
      if (!imagesList[position]) {
        imagesList[position] = {
          entityId: parseInt(entityId),
          url: null,
          order: position
        };
      }
      imagesList[position].url = downloadURL;

      console.log('✅ Lista preparada DEPOIS de adicionar nova imagem:', imagesList);
      console.log('📤 Payload que será enviado:', {
        entityId: parseInt(entityId),
        images: imagesList
      });

      // 4. ATUALIZAR VIA POST NA API
      await api.post(ENDPOINTS.ENTITIES_IMAGES, {
        entityId: parseInt(entityId),
        images: imagesList
      });

      return {
        success: true,
        url: downloadURL,
        images: imagesList
      };
    } catch (error) {
      console.error('Erro ao fazer upload da foto:', error);
      return { 
        success: false, 
        error: error.response?.data?.msg || error.response?.data?.message || error.message || 'Erro ao enviar foto. Tente novamente.' 
      };
    }
  },

  /**
   * Constrói lista de 5 posições a partir das imagens retornadas pela API
   * @param {Array} apiImages - Array de imagens retornado pela API (com id, entityId, url, order, active, createdAt, updatedAt)
   * @param {string|number} entityId - ID da entidade
   * @returns {Array} Array com 5 objetos {entityId, url, order}
   */
  buildImagesList(apiImages, entityId) {
    // Inicializar array de 5 posições com objetos válidos
    const list = [];
    for (let i = 0; i < 5; i++) {
      list[i] = {
        entityId: parseInt(entityId),
        url: null,
        order: i
      };
    }
    
    // Preencher com as URLs das imagens existentes
    if (Array.isArray(apiImages) && apiImages.length > 0) {
      apiImages.forEach(img => {
        if (img && typeof img.order === 'number' && img.order >= 0 && img.order <= 4) {
          list[img.order] = {
            entityId: parseInt(entityId),
            url: img.url || null,
            order: img.order
          };
        }
      });
    }
    
    return list;
  },

  /**
   * Deleta uma foto da entidade (remove URL da posição)
   * @param {string} entityId - ID da entidade
   * @param {number} position - Posição da imagem a remover (0-4)
   * @param {Array} currentImages - Array atual de imagens da entidade
   * @returns {Promise<{success: boolean, images?: Array, error?: string}>}
   */
  async deletePhoto(entityId, position, currentImages) {
    try {
      if (position < 0 || position > 4) {
        return {
          success: false,
          error: 'Posição inválida. Use um valor entre 0 e 4.'
        };
      }

      // 1. PREPARAR LISTA COMPLETA DE 5 POSIÇÕES
      const imagesList = this.buildImagesList(currentImages, entityId);
      
      // 2. COLETAR TODAS AS URLs NÃO NULAS, EXCETO A DA POSIÇÃO A SER DELETADA
      const remainingUrls = imagesList
        .filter((img, idx) => idx !== position && img.url !== null)
        .map(img => img.url);

      // 3. REORGANIZAR AS URLS NAS POSIÇÕES (MOVER PARA CIMA)
      const newImagesList = [];
      for (let i = 0; i < 5; i++) {
        newImagesList[i] = {
          entityId: parseInt(entityId),
          url: remainingUrls[i] || null,
          order: i
        };
      }

      // 4. ATUALIZAR VIA POST NA API
      await api.post(ENDPOINTS.ENTITIES_IMAGES, {
        entityId: parseInt(entityId),
        images: newImagesList
      });
      
      return {
        success: true,
        images: newImagesList
      };
    } catch (error) {
      console.error('Erro ao deletar foto:', error);
      return { 
        success: false, 
        error: error.response?.data?.msg || error.response?.data?.message || 'Erro ao deletar foto. Tente novamente.' 
      };
    }
  },

  /**
   * Faz upload de avatar de perfil do usuário para o Firebase
   * 
   * @param {File} file - Arquivo da foto
   * @param {string} userId - ID do usuário
   * @returns {Promise<{success: boolean, url?: string, error?: string}>}
   */
  async uploadProfileAvatar(file, userId) {
    try {
      // Validar tipo de arquivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        return { 
          success: false, 
          error: 'Formato inválido. Use JPG, PNG ou WEBP.' 
        };
      }

      // Validar tamanho (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        return { 
          success: false, 
          error: 'Arquivo muito grande. Tamanho máximo: 5MB.' 
        };
      }

      // 1. UPLOAD DO ARQUIVO PARA FIREBASE no caminho /profile
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storagePath = `profile/${fileName}`; // Usando /profile ao invés de /entities
      const storageRef = ref(storage, storagePath);

      console.log('📤 Uploading to:', storagePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      console.log('✅ Avatar uploaded successfully:', downloadURL);

      return {
        success: true,
        url: downloadURL
      };
    } catch (error) {
      console.error('Erro ao fazer upload do avatar:', error);
      return { 
        success: false, 
        error: error.response?.data?.msg || error.message || 'Erro ao enviar foto. Tente novamente.' 
      };
    }
  }
};
